import React, { FC } from 'react'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'

import { ColorSelector } from './ColorSelector'
import { IconSelector } from './IconSelector'
import { PreviewKnockStatus } from './PreviewKnockStatus'
import { useAddKnockStatusData } from './hooks/useData'
import { useAddKnockStatusHandlers } from './hooks/useHandlers'

interface AddKnockStatusDialogProps {
  open: boolean
  onClose: () => void
  editId?: string
}

export const AddKnockStatusDialog: FC<AddKnockStatusDialogProps> = ({
  open,
  onClose,
  editId,
}) => {
  const { initialValues, existingNames, isEditMode } =
    useAddKnockStatusData(editId)
  const { handleSubmit } = useAddKnockStatusHandlers(onClose)

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Required')
      .max(20, 'Max 20 chars')
      .test('unique', 'Name must be unique', (value) => {
        if (!value) return true
        return !existingNames.includes(value.toLowerCase().trim())
      }),
    color: Yup.string().required('Color Required'),
    icon: Yup.number().required('Icon Required'),
  })

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 600, px: 3, pt: 3 }}>
        {isEditMode ? 'Edit Knock Status' : 'Add Knock Status'}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange
        validateOnBlur
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
        }) => (
          <Form>
            <DialogContent
              sx={{
                px: 3,
                pb: 4,
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: '#F9FAFB',
              }}
            >
              <Box
                display="flex"
                alignItems="flex-start"
                minWidth={{ xs: '100%', sm: 564 }}
                flexDirection="column"
              >
                <Typography variant="subtitle3">Status Name*</Typography>
                <TextField
                  sx={{ mt: 1 }}
                  name="name"
                  fullWidth
                  placeholder="e.g., Not home, Interested"
                  value={values.name}
                  onChange={(e) => {
                    e.target.value = e.target.value.trimStart()
                    handleChange(e)
                    if (e.target.value.length > 0)
                      setFieldTouched('name', true, false)
                  }}
                  onBlur={handleBlur}
                  error={touched.name && !!errors.name}
                  helperText={
                    (touched.name && errors.name) || ` ${values.name.length}/20`
                  }
                  inputProps={{ maxLength: 20 }}
                  variant="outlined"
                  size="small"
                />

                <Grid container spacing={2} mt={0}>
                  <Grid item xs={12} sm={7}>
                    <Stack spacing={2}>
                      <ColorSelector
                        value={values.color}
                        onChange={(color) => setFieldValue('color', color)}
                        error={touched.color ? errors.color : undefined}
                      />

                      <IconSelector
                        value={values.icon}
                        onChange={(icon) => setFieldValue('icon', icon)}
                        error={touched.icon ? errors.icon : undefined}
                      />
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={5}>
                    <PreviewKnockStatus
                      name={values.name}
                      color={values.color}
                      icon={values.icon}
                    />
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button variant="text" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {isEditMode ? 'Save' : 'Create'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
